import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

import { displayError } from '@app/_utils/error-util';
import { IcashComparisonDetails, IcashComparisonLine } from '../../_models/icash-comparison-details.model';
import { IcashComparisonService } from '../../_services/icash-comparison.service';

export interface IcashComparisonDetailDialogData {
  /** The id of whatever the row is about - an inspection or a sale, per subjectType. */
  technicalInspectionId: number;
  /** Which side that id names. Absent means a technical inspection, as it always did. */
  subjectType?: 'TECHNICAL_INSPECTION' | 'SALE';
  /** The iCash OperationID, from the row the operator clicked. */
  operationId: number;
  /** Shown in the title until the comparison arrives. */
  registrationNumber: string;
}

/**
 * The line-by-line comparison behind one row.
 *
 * Both the receipt lines and the printed receipt number are fetched when the dialog opens: iCash
 * leaves fiscalNumber null in the list it serves the table from, and fills it in only on the
 * single-receipt call this makes.
 */
@Component({
  selector: 'app-icash-comparison-detail-dialog',
  templateUrl: './icash-comparison-detail-dialog.component.html',
  styleUrls: ['./icash-comparison-detail-dialog.component.css']
})
export class IcashComparisonDetailDialogComponent implements OnInit {

  details: IcashComparisonDetails;
  lines: IcashComparisonLine[] = [];

  dataLoaded = false;

  private errorMessageSubject = new Subject<string>();
  errorMessage$ = this.errorMessageSubject.asObservable();

  constructor(
    private dialogRef: MatDialogRef<IcashComparisonDetailDialogComponent, void>,
    @Inject(MAT_DIALOG_DATA) public data: IcashComparisonDetailDialogData,
    private translateService: TranslateService,
    private icashComparisonService: IcashComparisonService
  ) { }

  ngOnInit(): void {
    this.loadDetails();
  }

  close() {
    this.dialogRef.close();
  }

  reload() {
    this.errorMessageSubject.next(undefined);
    this.loadDetails();
  }

  /** Colours a line the way the table colours a row, so the two read the same. */
  lineStatusClass(lineStatus: string): string {
    switch (lineStatus) {
      case 'MATCHED':               return 'badge badge-success';
      case 'PRICE_DIFFERENT':       return 'badge badge-warning';
      case 'MISSING_IN_INSPECTION': return 'badge badge-danger';
      case 'MISSING_IN_RECEIPT':    return 'badge badge-secondary';
      default:                      return 'badge badge-light';
    }
  }

  lineStatusKey(lineStatus: string): string {
    return lineStatus ? 'icashComparison.lineStatus.' + lineStatus : '';
  }

  // Both take an optional status: the template reads it off details?, which is undefined until
  // the comparison arrives.
  matchStatusClass(matchStatus?: string): string {
    switch (matchStatus) {
      case 'MATCHED':         return 'badge badge-success';
      case 'DIFFERENT':       return 'badge badge-warning';
      case 'MISSING_RECEIPT': return 'badge badge-danger';
      default:                return 'badge badge-light';
    }
  }

  matchStatusKey(matchStatus?: string): string {
    return matchStatus ? 'icashComparison.status.' + matchStatus : '';
  }

  /**
   * Whether the number typed at the point is the one printed on the receipt.
   *
   * Compared with leading zeros stripped, the same way the backend pairs them: the operator types
   * what is on the slip (0017126) while iCash reports the number plainly (17126), and those are
   * the same receipt.
   */
  fiscalNumbersAgree(): boolean {
    const typed = this.stripLeadingZeros(this.details?.inspectionFiscalNumber);
    const printed = this.stripLeadingZeros(this.details?.fiscalNumber);

    return !!typed && !!printed && typed === printed;
  }

  private stripLeadingZeros(value?: string): string {
    const trimmed = (value || '').trim();
    return /^[0-9]+$/.test(trimmed) ? (trimmed.replace(/^0+/, '') || '0') : '';
  }

  private loadDetails() {
    this.dataLoaded = false;

    this.icashComparisonService.findDetails(this.data.technicalInspectionId, this.data.operationId,
        this.data.subjectType ?? 'TECHNICAL_INSPECTION').subscribe({
      next: details => {
        this.details = details;
        this.lines = details?.lines || [];
        this.dataLoaded = true;
      },
      error: err => {
        displayError(err);
        this.dataLoaded = false;
        this.errorMessageSubject.next(this.translateService.instant('messages.errorLoadingData'));
      }
    });
  }

}
